import React, { useEffect, useState, useRef } from 'react';
import '../../css/online-sales-team.css';
import language from '../../utils/LanguageSettings';
import { fetchOnlineSalesTeam } from '../functions/fetchOnlineSalesTeam';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import RenderSnA from '../functions/renderSnA';
import { SOURCE_URL } from '../../config';
import { useInView } from 'react-intersection-observer';
import 'animate.css';
import { animationClass, animationStyle } from '../../utils/FadeInEffect';
import salesTeamPlaceholder from '../../images/icons/sales-team-placeholder.png';
import ReactPaginate from 'react-paginate';

const TeamMember: React.FC<{
  item: any;
  globalIndex: number;
  animationKey: number;
  onErrorImage: (e: any) => void;
  isAnimated: boolean;
}> = ({ item, globalIndex, animationKey, onErrorImage, isAnimated }) => {

  return (
    <div
      key={`${animationKey}-${globalIndex}`}
      className={`col-lg-3 col-md-4 col-sm-6 mb-3 member-item ${animationClass(isAnimated, 'in')}`}
      style={animationStyle(isAnimated, 'in')}
    >
      <div className="h-100 light-gray-bg">
        <div className="position-relative">
          <img src={salesTeamPlaceholder} alt="" className="w-100 img-fluid img-placeholder" />
          {item?.FeaturedImage?.data?.attributes?.url && (
            <img
              src={SOURCE_URL + item?.FeaturedImage?.data?.attributes?.url}
              alt=""
              className="h-100 w-100 featured-img"
              onError={onErrorImage}
            />
          )}
        </div>
        <div className="p-3">
          {item?.NameEng}
          <span className="d-block">
            {item?.Nickname ? "(" + item?.Nickname + ")" : <>&nbsp;</>}
          </span>
          <span className="light-gray d-block py-3">{item?.NameChi}</span>
          {item?.ContactNo && (
            <a href={"tel:" + item?.ContactNo} className="d-block phone-link barlow-font light-gray mb-2">
              {item?.ContactNo}
            </a>
          )}
          {item?.FBUsername && (
            <a href={"https://www.facebook.com/" + item?.FBUsername} target="_blank" className="barlow-font light-gray d-block fb-link">
              {item?.FBUsername}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const OnlineSalesTeam: React.FC = () => {
  const { ref: ref1, inView: inView1 } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [endOffset, setEndOffset] = useState(itemOffset + itemsPerPage);
  const [currentItems, setCurrentItems] = useState<any>(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingStyle, setLoadingStyle] = useState<string>("none");
  const [loading, setLoading] = useState<boolean>(true);
  const [content, setContent] = useState<any>();
  const [members, setMembers] = useState<any>();
  const [seo, setSeo] = useState<any>(null);
  const [ga, setGa] = useState<any>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [isMemberAnimated, setIsMemberAnimated] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data = await fetchOnlineSalesTeam(language);
      if (data) {
        setContent(data?.attributes);
        setMembers(data?.attributes?.Members?.filter((item: { Status: string; }) => item.Status === "Active"));
        setSeo(data?.attributes?.SEO || null);
        setGa(data?.attributes?.GA || null);
        setItemsPerPage(data?.attributes?.LimitPerPage);
        setEndOffset(itemOffset + data?.attributes?.LimitPerPage);
      }
      setLoading(false);
      setIsMemberAnimated(true);
    };
    fetchData();
  }, [language]);

  useEffect(() => {
    setCurrentItems(members?.slice(itemOffset, endOffset));
    setPageCount(Math.ceil((members?.length || 0) / itemsPerPage));
  }, [members, itemsPerPage]);

  useEffect(() => {
    setCurrentItems(members?.slice(itemOffset, endOffset));
    setAnimationKey(prev => prev + 1);
    setIsMemberAnimated(false);
    setTimeout(() => {
      setIsMemberAnimated(true);
    }, 500);
  }, [itemOffset, members]);

  const handleErrorImage = (event: any) => {
    const img = event.target;
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    img.onerror = null;
  };

  const handlePageClick = (event: { selected: number }) => {
    setLoadingStyle("block");
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setTimeout(() => {
      const selectedPage = event.selected;
      const newOffset = selectedPage * itemsPerPage;
      setItemOffset(newOffset);
      setEndOffset(newOffset + itemsPerPage);
      setCurrentPage(selectedPage);
      setLoadingStyle("none");
    }, 500);
  };

  if (loading) {
    return (
      <>
        <div id="loader">
          <div className="spinner-border"></div>
        </div>
      </>
    );
  }

  const bannerImage = content?.Banner?.BannerImage?.data?.attributes?.url;
  const bannerImageTab = content?.Banner?.BannerImageTab?.data?.attributes?.url ?? bannerImage;
  const bannerImageMob = content?.Banner?.BannerImageMob?.data?.attributes?.url ?? bannerImageTab;

  const bannerImagePosition = content?.Banner?.BannerImagePosition?.toLowerCase();
  const bannerImagePositionTab = content?.Banner?.BannerImagePositionTab?.toLowerCase() ?? bannerImagePosition;
  const bannerImagePositionMob = content?.Banner?.BannerImagePositionMob?.toLowerCase() ?? bannerImagePositionTab;

  return (
    <>
      <RenderSnA seo={seo} ga={ga} />
      <section id="inner-banner" className='online-sales-teams'>
        <img
          src={SOURCE_URL + bannerImage}
          className={`w-100 img-fit ${content?.Banner?.BannerTextColor?.toLowerCase() === "light" ? "black-overlay" : "white-overlay"} d-xl-block d-none`}
          style={{ objectPosition: bannerImagePosition }}
        />
        <img
          src={SOURCE_URL + bannerImageTab}
          className={`w-100 img-fit ${content?.Banner?.BannerTextColor?.toLowerCase() === "light" ? "black-overlay" : "white-overlay"} d-xl-none d-lg-block d-none`}
          style={{ objectPosition: bannerImagePositionTab }}
        />
        <img
          src={SOURCE_URL + bannerImageMob}
          className={`w-100 img-fit ${content?.Banner?.BannerTextColor?.toLowerCase() === "light" ? "black-overlay" : "white-overlay"} d-lg-none d-block`}
          style={{ objectPosition: bannerImagePositionMob }}
        />
        <div className={`pxc animated banner-${content?.Banner?.BannerTextPosition?.toLowerCase()} banner-text ${content?.Banner?.BannerTextColor?.toLowerCase() === "light" ? "white-color" : ""}`}>
          <div ref={ref1} className={animationClass(inView1, 'up')} style={animationStyle(inView1, 'up')}>
            <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>
              {content?.Banner?.BannerText}
            </ReactMarkdown>
          </div>
        </div>
      </section>
      <section id="main-content" className="spacer-top online-sales-teams-member">
        <div className="container-fluid" style={{maxWidth:"1340px"}}>
          <div className="results-area position-relative">
            <div className='row row-sm'>
              <div id="loader" style={{ display: loadingStyle }}>
                <div className="spinner-border"></div>
              </div>
              {Array.isArray(currentItems) && currentItems.map((item: any, index: number) => (
                <TeamMember
                  key={`${animationKey}-${index}`}
                  item={item}
                  globalIndex={itemOffset + index}
                  animationKey={animationKey}
                  onErrorImage={handleErrorImage}
                  isAnimated={isMemberAnimated}
                />
              ))}
              {itemsPerPage < members.length ?
                <ReactPaginate
                  breakLabel="..."
                  nextLabel={currentPage < pageCount - 1 ? <i className="support-icon icon-now-arrow-right"></i> : null}
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={3}
                  pageCount={pageCount}
                  previousLabel={currentPage > 0 ? <i className="support-icon icon-now-arrow-left"></i> : null}
                  renderOnZeroPageCount={null}
                  containerClassName="support-pagination py-5"
                  pageClassName="support-page-item"
                  pageLinkClassName="support-page-link"
                  activeClassName="active"
                  previousClassName="support-page-item"
                  nextClassName="support-page-item"
                  previousLinkClassName="support-page-link"
                  nextLinkClassName="support-page-link"
                  disabledClassName="disabled"
                  forcePage={currentPage}
                />
                :<ul className='py-5'></ul>
              }
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OnlineSalesTeam;